/* Route-specific document titles (review #11).
 *
 * TitleBridge is the single writer of document.title (same pattern as the
 * <html lang> sync in i18n/provider): on every location change it matches the
 * pathname against TITLE_ROUTES — a standalone, title-only route tree — and
 * composes "{Sub} – {Section} – {appName}", deepest match first, so truncated
 * browser tabs keep the most specific part.
 *
 * TITLE_ROUTES deliberately duplicates the app's path patterns instead of
 * annotating the live route tables: the settings/admin sub-routes live in
 * nested useRoutes tables inside lazy-loaded views the top-level table can't
 * see. Titles are presentation-only, and a drifted pattern merely degrades to
 * the section (or bare appName) title. Titles reuse the nav/tab label message
 * ids wherever one exists, so tab titles stay consistent with the menu
 * automatically.
 */
import { defineMessage } from "@lingui/core/macro";
import { useContext, useEffect } from "react";
import { useLingui } from "@lingui/react";
import { matchRoutes, useLocation, RouteObject } from "react-router";
import type { MessageDescriptor } from "@lingui/core";
import { EnvironmentContext } from "./themes/environment";

// Reused ids carry their original German default verbatim — the extractor
// merges by id and complains about conflicting defaults.
const T = {
  login: defineMessage({ id: "title.login", message: "Login" }),
  dashboard: defineMessage({ id: "nav.dashboard", message: "Dashboard" }),
  timeline: defineMessage({
    id: "nav.scalecomparison",
    message: "Waagenvergleich",
  }),
  map: defineMessage({ id: "nav.map", message: "Karte" }),
  shop: defineMessage({ id: "nav.shop", message: "Shop" }),
  orders: defineMessage({ id: "nav.orders", message: "Bestellungen" }),
  registration: defineMessage({
    id: "title.registration",
    message: "Registrierung",
  }),
  password: defineMessage({
    id: "password.request_reset.title",
    message: "Passwort zurücksetzen",
  }),
  export: defineMessage({ id: "title.export", message: "Export" }),
  settings: defineMessage({ id: "nav.settings", message: "Einstellungen" }),
  // nav.admin's message embeds the crown-icon tag — unusable as plain text.
  admin: defineMessage({ id: "title.admin", message: "Admin" }),
  notFound: defineMessage({
    id: "title.not_found",
    message: "Seite nicht gefunden",
  }),
  // settings tabs (SettingsView labels)
  settingsControllers: defineMessage({
    id: "settings.settingsview.controllers",
    message: "Funkstationen",
  }),
  settingsNotifications: defineMessage({
    id: "settings.settingsview.notifications",
    message: "Benachrichtigungen",
  }),
  settingsWatches: defineMessage({
    id: "settings.settingsview.watches",
    message: "Funkstationen Beobachten",
  }),
  settingsShares: defineMessage({
    id: "settings.settingsview.sharing",
    message: "Freigaben",
  }),
  settingsProfile: defineMessage({
    id: "settings.settingsview.profile",
    message: "Profil",
  }),
  scaleEdit: defineMessage({
    id: "settings.scale_list.header_edit",
    message: "Sensor bearbeiten",
  }),
  scaleNew: defineMessage({
    id: "settings.scale_list.header_new",
    message: "Neuer Sensor",
  }),
  // admin/manager sections (NavigationBar dropdown labels)
  adminUsers: defineMessage({
    id: "settings.settingsview.usermanagement",
    message: "Benutzerverwaltung",
  }),
  adminControllers: defineMessage({
    id: "nav.controllers",
    message: "Funkstationen",
  }),
  adminControllersCreate: defineMessage({
    id: "settings.settingsview.registercontrollers",
    message: "Funkstationen Registrieren",
  }),
  adminJobs: defineMessage({
    id: "settings.settingsview.jobs",
    message: "Jobs",
  }),
  adminFirmware: defineMessage({
    id: "settings.settingsview.firmware",
    message: "Firmware",
  }),
  adminVariables: defineMessage({
    id: "nav.admin.variables",
    message: "Variablen",
  }),
  adminRegions: defineMessage({ id: "nav.regions", message: "Regionen" }),
  adminMap: defineMessage({ id: "nav.admin.map", message: "Karte" }),
  affiliateNotification: defineMessage({
    id: "nav.affiliateNotification",
    message: "Kunden Benachrichtigen",
  }),
};

type TitleHandle = { title?: MessageDescriptor };
const h = (title: MessageDescriptor): TitleHandle => ({ title });

/* Handle-less children ({ path: "*" }) keep unlisted sub-pages at their
   section's title instead of falling through to the global 404 entry. */
// eslint-disable-next-line react-refresh/only-export-components -- title data shared with computeTitle/tests
export const TITLE_ROUTES: RouteObject[] = [
  { index: true, handle: h(T.login) },
  { path: "dashboard", handle: h(T.dashboard) },
  { path: "timeline", handle: h(T.timeline) },
  { path: "scaleoverview/:expand?", handle: h(T.timeline) },
  { path: "registration", handle: h(T.registration) },
  { path: "password/*", handle: h(T.password) },
  { path: "export", handle: h(T.export) },
  { path: "shop/admin", handle: h(T.orders) },
  { path: "shop/*", handle: h(T.shop) },
  { path: "subscriptionShop/*", handle: h(T.shop) },
  { path: "community/:area/map", handle: h(T.map) },
  { path: "community/:area/timeline/:controllerId?", handle: h(T.timeline) },
  {
    // shared/public chart views title as Waagenvergleich too
    path: "shared",
    children: [
      { path: "timeline", handle: h(T.timeline) },
      { path: "livefeed", handle: h(T.timeline) },
      { path: "*" },
    ],
  },
  {
    path: "bs/:area",
    children: [
      { path: "map", handle: h(T.map) },
      { path: "timeline/:controllerId?", handle: h(T.timeline) },
      { path: "*" },
    ],
  },
  {
    path: "settings",
    handle: h(T.settings),
    children: [
      { path: "controllers", handle: h(T.settingsControllers) },
      { path: "controllers/add", handle: h(T.settingsControllers) },
      { path: "controllers/:id/*", handle: h(T.settingsControllers) },
      { path: "watches", handle: h(T.settingsWatches) },
      { path: "scales/add/:combinedId?", handle: h(T.scaleNew) },
      { path: "scales/:id", handle: h(T.scaleEdit) },
      { path: "notifications", handle: h(T.settingsNotifications) },
      { path: "shares", handle: h(T.settingsShares) },
      { path: "users", handle: h(T.settingsProfile) },
      { path: "*" },
    ],
  },
  {
    path: "a",
    handle: h(T.admin),
    children: [
      { path: "users", handle: h(T.adminUsers) },
      { path: "notifications", handle: h(T.settingsNotifications) },
      { path: "controllers", handle: h(T.adminControllers) },
      { path: "controllers/create", handle: h(T.adminControllersCreate) },
      { path: "jobs", handle: h(T.adminJobs) },
      { path: "firmware", handle: h(T.adminFirmware) },
      { path: "variables", handle: h(T.adminVariables) },
      { path: "regions", handle: h(T.adminRegions) },
      { path: "map/:area", handle: h(T.adminMap) },
      { path: "*" },
    ],
  },
  {
    path: "m",
    handle: h(T.admin),
    children: [
      { path: "users", handle: h(T.adminUsers) },
      { path: "controllers", handle: h(T.adminControllers) },
      { path: "affiliateNotification", handle: h(T.affiliateNotification) },
      { path: "*" },
    ],
  },
  // transient / no title of their own
  { path: "logout" },
  { path: "*", handle: h(T.notFound) },
];

// eslint-disable-next-line react-refresh/only-export-components -- pure compose fn shared with tests
export function computeTitle(
  pathname: string,
  i18n: { _: (descriptor: MessageDescriptor) => string },
  appName: string,
): string {
  const matches = matchRoutes(TITLE_ROUTES, pathname) ?? [];
  const titles = matches
    .map((m) => (m.route.handle as TitleHandle | undefined)?.title)
    .filter((d): d is MessageDescriptor => Boolean(d))
    .reverse()
    .map((d) => i18n._(d));
  return [...titles, appName].join(" – ");
}

export function TitleBridge() {
  const { pathname } = useLocation();
  const { i18n } = useLingui(); // re-renders on locale switch
  const { appName } = useContext(EnvironmentContext);
  const title = computeTitle(pathname, i18n, appName);
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
}
